'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Image as ImageIcon, FileText, Download, X } from 'lucide-react';
import { Message } from './types';
import { createClient } from '@/lib/supabase/client';
import { FileText as FileTextIcon, FileSpreadsheet, FileImage, FileArchive, File } from 'lucide-react';

const supabase = createClient();

function buildConversationId(a: string, b: string): string {
  return [a, b].sort().join('_');
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${m} ${ampm}`;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}

function FileIcon({ mime, size }: { mime?: string; size: number }) {
  if (!mime) return <File size={size} className="flex-shrink-0" />;
  if (mime.startsWith('image/')) return <FileImage size={size} className="flex-shrink-0" />;
  if (mime.includes('pdf') || mime.includes('word') || mime.includes('text')) return <FileTextIcon size={size} className="flex-shrink-0" />;
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return <FileSpreadsheet size={size} className="flex-shrink-0" />;
  if (mime.includes('zip') || mime.includes('archive')) return <FileArchive size={size} className="flex-shrink-0" />;
  return <File size={size} className="flex-shrink-0" />;
}

type Tab = 'media' | 'files';

interface MediaItem {
  id: string;
  type: 'image' | 'video' | 'file' | 'audio';
  url: string;
  name?: string;
  size?: number;
  mime?: string;
  timestamp: string;
  senderId: string;
}

interface MediaGalleryScreenProps {
  chatId?: string;
  localUserId?: string;
  remoteUserId?: string;
  remoteUserName?: string;
  messages?: Message[];
  onBack?: () => void;
}

function groupByDate(items: MediaItem[]): { date: string; items: MediaItem[] }[] {
  const map = new Map<string, MediaItem[]>();
  for (const item of items) {
    const key = formatDate(item.timestamp);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }
  return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
}

export default function MediaGalleryScreen({
  chatId,
  localUserId,
  remoteUserId,
  remoteUserName = 'Chat',
  messages: propMessages,
  onBack,
}: MediaGalleryScreenProps) {
  const [tab, setTab] = useState<Tab>('media');
  const [allMessages, setAllMessages] = useState<Message[]>(propMessages ?? []);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!propMessages);

  const conversationId = localUserId && remoteUserId
    ? buildConversationId(localUserId, remoteUserId)
    : chatId ?? 'default';

  useEffect(() => {
    if (propMessages) {
      setAllMessages(propMessages);
      return;
    }
    if (!localUserId || !remoteUserId) return;

    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false });

      if (!error && data) {
        const mapped: Message[] = data.map((row) => ({
          id: row.id as string,
          senderId: row.sender_id === localUserId ? 'me' : 'alex',
          text: (row.text as string | null) ?? undefined,
          imageUrl: (row.image_url as string | null) ?? undefined,
          imageAlt: (row.image_alt as string | null) ?? undefined,
          audioUrl: (row.audio_url as string | null) ?? undefined,
          audioDuration: (row.audio_duration as number | null) ?? undefined,
          fileUrl: (row.file_url as string | null) ?? undefined,
          fileName: (row.file_name as string | null) ?? undefined,
          fileSize: (row.file_size as number | null) ?? undefined,
          fileMime: (row.file_mime as string | null) ?? undefined,
          timestamp: row.created_at as string,
          status: (row.msg_status as Message['status']) ?? 'sent',
          reactions: [],
          type: (row.msg_type as Message['type']) ?? 'text',
        }));
        setAllMessages(mapped);
      }
      setLoading(false);
    };
    load();
  }, [conversationId, localUserId, remoteUserId, propMessages]);

  // Derive media items from messages
  const mediaItems: MediaItem[] = allMessages
    .filter((m) => m.type === 'image' && m.imageUrl)
    .map((m) => ({
      id: m.id,
      type: 'image' as const,
      url: m.imageUrl!,
      name: m.imageAlt ?? 'Image',
      timestamp: m.timestamp,
      senderId: m.senderId,
    }))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const fileItems: MediaItem[] = allMessages
    .filter((m) => (m.type === 'file' && m.fileUrl) || (m.type === 'audio' && m.audioUrl))
    .map((m) => ({
      id: m.id,
      type: m.type === 'audio' ? 'audio' as const : 'file' as const,
      url: (m.fileUrl ?? m.audioUrl)!,
      name: m.fileName ?? (m.type === 'audio' ? 'Voice message' : 'File'),
      size: m.fileSize,
      mime: m.fileMime ?? (m.type === 'audio' ? 'audio/webm' : undefined),
      timestamp: m.timestamp,
      senderId: m.senderId,
    }))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const mediaGroups = groupByDate(mediaItems);
  const fileGroups = groupByDate(fileItems);

  return (
    <div
      className="flex flex-col h-screen w-full overflow-hidden"
      style={{ background: 'var(--background)' }}
    >
      {/* Header */}
      <header
        className="chat-header-surface flex items-center gap-2.5 px-2 sm:px-3 flex-shrink-0 z-20 relative"
        style={{ height: '64px', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      >
        <button
          onClick={onBack}
          className="flex items-center justify-center w-[44px] h-[44px] rounded-full hover:bg-white/8 transition-colors duration-150 active:scale-95 flex-shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft size={22} className="text-white" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-[17px] font-semibold text-white leading-tight truncate">{remoteUserName}</p>
          <p className="text-[12px]" style={{ color: '#91A3B7' }}>Shared Media &amp; Files</p>
        </div>
      </header>

      {/* Tabs */}
      <div
        className="flex flex-shrink-0 border-b"
        style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
      >
        <TabButton
          active={tab === 'media'}
          onClick={() => setTab('media')}
          icon={<ImageIcon size={15} />}
          label="Media"
          count={mediaItems.length}
        />
        <TabButton
          active={tab === 'files'}
          onClick={() => setTab('files')}
          icon={<FileText size={15} />}
          label="Files"
          count={fileItems.length}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2.5">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" />
            </svg>
          </div>
        ) : tab === 'media' ? (
          mediaGroups.length === 0 ? (
            <EmptyState icon={<ImageIcon size={40} />} label="No photos or videos shared yet" />
          ) : (
            <div className="pb-6">
              {mediaGroups.map(({ date, items }) => (
                <div key={date}>
                  <DateLabel label={date} />
                  <div className="grid grid-cols-3 gap-0.5 px-0.5">
                    {items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setLightboxUrl(item.url)}
                        className="relative aspect-square overflow-hidden focus:outline-none group"
                        aria-label={`View ${item.name ?? 'image'}`}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={item.url}
                          alt={item.name ?? 'Shared image'}
                          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200" />
                        <span
                          className="absolute bottom-1 right-1 text-[10px] text-white/80 bg-black/40 rounded px-1 py-0.5 leading-none"
                        >
                          {formatTime(item.timestamp)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          fileGroups.length === 0 ? (
            <EmptyState icon={<FileText size={40} />} label="No files shared yet" />
          ) : (
            <div className="pb-6">
              {fileGroups.map(({ date, items }) => (
                <div key={date}>
                  <DateLabel label={date} />
                  <div className="flex flex-col divide-y" style={{ borderColor: 'var(--border)' }}>
                    {items.map((item) => (
                      <FileRow key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            onClick={() => setLightboxUrl(null)}
            aria-label="Close"
          >
            <X size={20} />
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={lightboxUrl}
            alt="Full size preview"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <a
            href={lightboxUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-6 right-6 flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium transition-all active:scale-95"
            style={{ background: 'var(--primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Download size={16} />
            Download
          </a>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex items-center justify-center gap-2 py-3 text-[13.5px] font-medium transition-colors relative"
      style={{ color: active ? 'var(--primary)' : '#91A3B7' }}
    >
      {icon}
      {label}
      {count > 0 && (
        <span
          className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full leading-none"
          style={{
            background: active ? 'var(--primary)' : 'rgba(145,163,183,0.2)',
            color: active ? '#fff' : '#91A3B7',
          }}
        >
          {count}
        </span>
      )}
      {active && (
        <span
          className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full"
          style={{ background: 'var(--primary)' }}
        />
      )}
    </button>
  );
}

function DateLabel({ label }: { label: string }) {
  return (
    <div className="px-4 py-2 mt-2">
      <span
        className="text-[12px] font-semibold uppercase tracking-wider"
        style={{ color: '#91A3B7' }}
      >
        {label}
      </span>
    </div>
  );
}

function FileRow({ item }: { item: MediaItem }) {
  const ext = item.name ? getFileExtension(item.name) : 'FILE';
  const sizeStr = formatFileSize(item.size);

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div
        className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(46,166,255,0.15)' }}
      >
        <FileIcon mime={item.mime} size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-medium text-white truncate">{item.name}</p>
        <p className="text-[11.5px] mt-0.5" style={{ color: '#91A3B7' }}>
          {[ext, sizeStr, formatTime(item.timestamp)].filter(Boolean).join(' · ')}
        </p>
      </div>
      <a
        href={item.url}
        download={item.name}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-90 hover:bg-white/10"
        aria-label={`Download ${item.name}`}
      >
        <Download size={16} className="text-white/70" />
      </a>
    </div>
  );
}

function EmptyState({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 px-8 text-center">
      <div style={{ color: '#91A3B7', opacity: 0.5 }}>{icon}</div>
      <p className="text-[14px]" style={{ color: '#91A3B7' }}>{label}</p>
    </div>
  );
}
